import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";

const monthNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmt = (minor, currency = "MYR") => {
  const n = (minor || 0) / 100;
  return `${currency} ${n.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export function SstReport() {
  const [month, setMonth] = useState(monthNow());
  const [includeExempt, setIncludeExempt] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/reports/sst", {
        params: { month, currency: "MYR", include_exempt: includeExempt },
      });
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [month, includeExempt]);

  useEffect(() => { load(); }, [load]);

  const exportPdf = () => {
    const url = `${API}/reports/sst/export.pdf?month=${encodeURIComponent(month)}&currency=MYR&include_exempt=${includeExempt}`;
    window.open(url, "_blank");
  };
  const exportCsv = () => {
    const url = `${API}/reports/sst/export.csv?month=${encodeURIComponent(month)}&currency=MYR&include_exempt=${includeExempt}`;
    window.open(url, "_blank");
  };

  const summary = data?.summary;
  const rows = data?.rows || [];
  const company = data?.company;
  const kastam = summary?.kastam_submission;

  const monthLabel = useMemo(() => {
    if (!month) return "";
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString("en-MY", { month: "long", year: "numeric" });
  }, [month]);

  return (
    <div className="space-y-6" data-testid="sst-report-section">
      {/* Filters */}
      <section className="bg-white border border-slate-200 rounded-md p-5 flex flex-wrap items-end gap-4" data-testid="sst-filters">
        <div>
          <Label className="text-xs uppercase text-slate-500 mb-1 block">Reporting month</Label>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-44 font-mono text-sm"
            data-testid="sst-month-input"
            max={monthNow()}
          />
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500 mb-1 block">Currency</Label>
          <div className="flex items-center h-10 px-3 border border-slate-200 rounded bg-slate-50 text-sm font-mono" data-testid="sst-currency-fixed">MYR only</div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase text-slate-500">Include SST-exempt (0%)</Label>
          <div className="flex items-center gap-2 h-10">
            <Switch
              checked={includeExempt}
              onCheckedChange={setIncludeExempt}
              data-testid="sst-include-exempt"
            />
            <span className="text-xs text-slate-600">{includeExempt ? "Shown" : "Hidden"}</span>
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={exportPdf} disabled={loading || !data} data-testid="sst-export-pdf"><FileText className="w-4 h-4 mr-1.5" />Export PDF</Button>
          <Button variant="outline" onClick={exportCsv} disabled={loading || !data} data-testid="sst-export-csv"><Download className="w-4 h-4 mr-1.5" />Export CSV</Button>
        </div>
      </section>

      {error && <div role="alert" className="border border-rose-200 bg-rose-50 text-rose-800 rounded p-3 text-sm" data-testid="sst-error">{error}</div>}
      {loading && !data && <p role="status" className="text-slate-500 text-sm" data-testid="sst-loading">Loading SST report…</p>}

      {summary && (
        <>
          {/* Kastam submission card */}
          <section className="border-2 border-slate-900 bg-slate-50 rounded-md p-5" data-testid="sst-kastam-card">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Kastam SST-02 submission summary</div>
                <div className="text-lg font-heading font-bold" data-testid="sst-kastam-period">{monthLabel}</div>
              </div>
              <Badge variant="outline" className="border-slate-900 text-slate-900" data-testid="sst-kastam-due">Due {kastam?.due_date}</Badge>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><dt className="text-slate-500 text-xs">Reporting period</dt><dd className="font-mono">{kastam?.period}</dd></div>
              <div><dt className="text-slate-500 text-xs">Rate(s) applied</dt><dd className="font-mono">{kastam?.sst_rate_note}</dd></div>
              <div><dt className="text-slate-500 text-xs">Taxable amount (MYR)</dt><dd className="font-mono font-bold text-lg" data-testid="sst-kastam-taxable">{fmt(kastam?.taxable_amount_minor).replace("MYR ", "")}</dd></div>
              <div><dt className="text-slate-500 text-xs">SST payable (MYR)</dt><dd className="font-mono font-bold text-lg text-emerald-800" data-testid="sst-kastam-payable">{fmt(kastam?.sst_payable_minor).replace("MYR ", "")}</dd></div>
            </dl>
          </section>

          {/* Summary strip */}
          <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sst-summary">
            <StatCard label="Reporting period" value={monthLabel} testId="sst-stat-period" mono={false} />
            <StatCard label="Total gross invoiced" value={fmt(summary.total_gross_minor)} testId="sst-stat-gross" />
            <StatCard label="Total SST collected" value={fmt(summary.total_sst_minor)} testId="sst-stat-sst" tone="emerald" />
            <StatCard label="Total net amount" value={fmt(summary.total_net_minor)} testId="sst-stat-net" />
            <StatCard label="Invoices in period" value={String(summary.invoice_count)} testId="sst-stat-count" mono={false} />
          </section>

          {/* Company header */}
          <p className="text-xs text-slate-500" data-testid="sst-company-header">
            <span className="font-semibold">{company?.name}</span> · TIN <span className="font-mono">{company?.tin}</span> · SST No <span className="font-mono">{company?.sst_no}</span>
          </p>

          {/* Detail table */}
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <Table data-testid="sst-detail-table">
              <TableHeader><TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Gross (MYR)</TableHead>
                <TableHead className="text-right">SST (MYR)</TableHead>
                <TableHead className="text-right">Net (MYR)</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500" data-testid="sst-empty">No paid MYR invoices in {monthLabel}.</TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.invoice_id} data-testid={`sst-row-${r.invoice_id}`}>
                    <TableCell className="font-mono text-xs">{r.number}</TableCell>
                    <TableCell className="font-mono text-xs">{r.date_issued}</TableCell>
                    <TableCell className="text-sm">{r.buyer_name}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.sst_rate}%</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{fmt(r.gross_minor).replace("MYR ", "")}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-emerald-800">{fmt(r.sst_minor).replace("MYR ", "")}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{fmt(r.net_minor).replace("MYR ", "")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {rows.length > 0 && (
                <TableFooter>
                  {summary.subtotals_by_rate.map((b) => (
                    <TableRow key={b.sst_rate} className="bg-amber-50" data-testid={`sst-subtotal-${b.sst_rate}`}>
                      <TableCell colSpan={3} className="text-right font-semibold text-xs">Subtotal SST {b.sst_rate}% ({b.invoices} invoice{b.invoices === 1 ? "" : "s"})</TableCell>
                      <TableCell />
                      <TableCell className="text-right font-mono tabular-nums font-semibold">{fmt(b.gross_minor).replace("MYR ", "")}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums font-semibold text-emerald-800">{fmt(b.sst_minor).replace("MYR ", "")}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums font-semibold">{fmt(b.net_minor).replace("MYR ", "")}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-100" data-testid="sst-grand-total">
                    <TableCell colSpan={4} className="text-right font-bold uppercase text-xs tracking-wider">Grand total</TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-bold">{fmt(summary.total_gross_minor).replace("MYR ", "")}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-bold text-emerald-800">{fmt(summary.total_sst_minor).replace("MYR ", "")}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-bold">{fmt(summary.total_net_minor).replace("MYR ", "")}</TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
          <p className="text-[11px] text-slate-500">
            SST is charged on services rendered in Malaysia. SGD transactions are excluded. Values are read from stored invoice totals — no invoice or SST calculation is re-computed for this report.
          </p>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, testId, tone = "slate", mono = true }) {
  const toneClass = {
    slate: "border-slate-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50",
  }[tone];
  return (
    <div className={`border rounded p-3 ${toneClass}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`${mono ? "font-mono" : ""} text-base font-bold mt-1 truncate`}>{value}</div>
    </div>
  );
}
